import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  fetchEmployees,
  createEmployee,
  updateEmployee,
  deleteEmployee,
  createEmployeeWorkHour,
  fetchEmployeeWorkHours,
  deleteEmployeeWorkHour,
  updateEmployeeWorkHour,
  fetchDaysEmployee,
  updatePasswordEmployee,
  uploadEmployeeImage,
} from "./thunks";
import { User } from "@/types";
import { toast } from "react-toastify";

interface PaginatedEmployees {
  data: User[];
  total?: number;
  currentPage?: number;
  perPage?: number;
}

interface EmployeesState {
  employees: PaginatedEmployees;
  workHours: any[]; // ou defina um tipo `EmployeeWorkHour[]` se tiver
  daysOfWeek: any[]; // ajuste o tipo conforme necessário
  loading: boolean;
  error: string | null;
  fetched: boolean;
}
const initialState: EmployeesState = {
  employees: { data: [] },
  workHours: [],
  daysOfWeek: [],
  loading: false,
  error: null,
  fetched: false,
};

const EmployeeSlice = createSlice({
  name: "Employees",
  initialState,
  reducers: {
    clearSuppliers: (state) => {
      state.employees = { data: [] };
      state.loading = false;
      state.error = null;
      state.fetched = false;
    },
    resetFetched: (state) => {
      state.fetched = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch
      .addCase(fetchEmployees.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchEmployees.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.fetched = true;
          const payload = action.payload;

          if (Array.isArray(payload.data)) {
            // Dados não paginados
            state.employees = {
              data: payload.data,
            };
          } else {
            // Dados paginados
            state.employees = {
              data: payload.data.data,
              total: payload.data.total,
              currentPage: payload.data.current_page,
              perPage: payload.data.per_page,
            };
          }
        },
      )
      .addCase(fetchEmployees.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.loading = false;
        state.fetched = false;
      })
      // Create
      .addCase(createEmployee.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        createEmployee.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.employees.data.push(action.payload.data);
          toast.success("Funcionário cadastrado com sucesso!");
        },
      )
      .addCase(createEmployee.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.loading = false;
        toast.error(
          (action.payload as { error: string }).error ||
            "Não foi possível cadastrar o funcionário, tente novamente!",
        );
      })

      // Update
      .addCase(updateEmployee.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        updateEmployee.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          const index = state.employees.data.findIndex(
            (s) => s.id == action.payload.data.id,
          );
          if (index !== -1) {
            state.employees.data[index] = action.payload.data;
          }
          toast.success("Funcionário atualizado com sucesso!");
        },
      )
      .addCase(updateEmployee.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.loading = false;
        toast.error(
          (action.payload as { error: string }).error ||
            "Não foi possível atualizar o funcionário, tente novamente!",
        );
      })

      // Update Password
      .addCase(updatePasswordEmployee.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        updatePasswordEmployee.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          const index = state.employees.data.findIndex(
            (s) => s.id == action.payload.data.id,
          );
          if (index !== -1) {
            state.employees.data[index] = action.payload.data;
          }
          toast.success("Senha atualizada com sucesso!");
        },
      )
      .addCase(updatePasswordEmployee.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.loading = false;
        toast.error(
          (action.payload as { error: string }).error ||
            "Não foi possível atualizar a senha do funcionário, tente novamente!",
        );
      })

      // Delete
      .addCase(deleteEmployee.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        deleteEmployee.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.employees.data = state.employees.data.filter(
            (s) => String(s.id) != action.payload.data.id,
          );
          toast.success("Funcionário excluído com sucesso!");
        },
      )
      .addCase(deleteEmployee.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.loading = false;
      })

      // Buscar horas do funcionário
      .addCase(fetchEmployeeWorkHours.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchEmployeeWorkHours.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.workHours = action.payload.data; // ou action.payload diretamente, depende da sua API
        },
      )
      .addCase(fetchEmployeeWorkHours.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as { error: string }).error;
      })

      // Cadastrar hora de trabalho
      .addCase(createEmployeeWorkHour.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        createEmployeeWorkHour.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.workHours.push(action.payload.data); // ou action.payload

          // Atualizar também o employee_work_hours no array de employees se tiver employeeId
          if (action.payload.data && action.payload.data.employee_id) {
            const employeeIndex = state.employees.data.findIndex(
              (emp) => emp.id == action.payload.data.employee_id,
            );
            if (employeeIndex !== -1) {
              if (!state.employees.data[employeeIndex].employee_work_hours) {
                state.employees.data[employeeIndex].employee_work_hours = [];
              }
              state.employees.data[employeeIndex].employee_work_hours!.push(
                action.payload.data,
              );
            }
          }

          toast.success("Hora de trabalho cadastrada com sucesso!");
        },
      )
      .addCase(createEmployeeWorkHour.rejected, (state, action) => {
        state.loading = false;
        console.log("action.payload", action.payload);

        state.error = (action.payload as { error: string }).error;
        toast.error(
          (action.payload as { error: string }).error ||
            "Erro ao cadastrar hora de trabalho",
        );
      })

      // Deletar hora de trabalho
      .addCase(deleteEmployeeWorkHour.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        deleteEmployeeWorkHour.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          // Atualiza com a lista retornada pela API após a remoção
          state.workHours = action.payload.data;

          // Os dados completos do funcionário serão recarregados pelo hook
          // então não precisamos atualizar manualmente aqui

          toast.success("Hora de trabalho removida com sucesso!");
        },
      )
      .addCase(deleteEmployeeWorkHour.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as { error: string }).error;
        toast.error(
          (action.payload as { error: string }).error ||
            "Erro ao remover hora de trabalho!",
        );
      })

      // Atualizar hora de trabalho (edição)
      .addCase(updateEmployeeWorkHour.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        updateEmployeeWorkHour.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          const updated = action.payload.data; // horário atualizado
          // Atualiza workHours global
          const idx = state.workHours.findIndex(
            (wh: any) => wh.id == updated.id,
          );
          if (idx !== -1) {
            state.workHours[idx] = updated;
          }

          // Atualiza também dentro do funcionário correspondente
          const employeeIndex = state.employees.data.findIndex(
            (emp) => emp.id == updated.employee_id,
          );
          if (
            employeeIndex !== -1 &&
            state.employees.data[employeeIndex].employee_work_hours
          ) {
            const whIndex = state.employees.data[
              employeeIndex
            ].employee_work_hours!.findIndex((wh: any) => wh.id == updated.id);
            if (whIndex !== -1) {
              state.employees.data[employeeIndex].employee_work_hours![
                whIndex
              ] = updated;
            }
          }
          toast.success("Horário de trabalho atualizado com sucesso!");
        },
      )
      .addCase(updateEmployeeWorkHour.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as { error: string }).error;
        toast.error(
          (action.payload as { error: string }).error ||
            "Erro ao atualizar horário de trabalho!",
        );
      })

      // Fetch dias da semana do funcionário
      .addCase(fetchDaysEmployee.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchDaysEmployee.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.daysOfWeek = action.payload.data;
        },
      )
      .addCase(fetchDaysEmployee.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as { error: string }).error;
        toast.error(
          (action.payload as { error: string }).error ||
            "Erro ao carregar dias da semana!",
        );
      })

      // Upload Employee Image
      .addCase(uploadEmployeeImage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        uploadEmployeeImage.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          const index = state.employees.data.findIndex(
            (s) => s.id == action.payload.data.id,
          );
          if (index !== -1) {
            state.employees.data[index] = action.payload.data;
          }
          toast.success("Imagem do funcionário atualizada com sucesso!");
        },
      )
      .addCase(uploadEmployeeImage.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as { error: string }).error;
        toast.error(
          (action.payload as { error: string }).error ||
            "Erro ao fazer upload da imagem!",
        );
      });
  },
});

export const { clearSuppliers, resetFetched } = EmployeeSlice.actions;
export default EmployeeSlice.reducer;
